/* eslint-disable @typescript-eslint/indent */
import useModal from '@ctn/ui/components/ctn-modal/hooks/useModal';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom';

import DocumentosListado from '@/components/DocumentosListado';
import GroupButtons, { type ButtonOfGroup } from '@/components/GroupButtons';
import IntervinientesListado from '@/components/IntervinientesListado';
import ModalCambiarVencimiento from '@/components/ModalCambiarVencimiento';
import ModalCancelacion from '@/components/ModalCancelacion';
import ModalHistorialSolicitud from '@/components/ModalHistorialSolicitud';
import ModalSubsanacionDocumentos from '@/components/ModalSubsanacionDocumentos';
import ModalSubsanacionInterviniente from '@/components/ModalSubsanacionInterviniente';
import ModalVisorPdf from '@/components/ModalVisorPdf';
import PageHeader from '@/components/PageHeader';
import SolicitudesDocumentacionListado from '@/components/SolicitudesDocumentacionListado';
import { getColumnsTramite } from '@/components/SolicitudesDocumentacionListado/columns';
import TextIcon from '@/components/TextIcon';
import { useGlobalContext } from '@/context/global.context';
import { usePageContext } from '@/context/page.context';
import useLoadTramite from '@/hooks/useLoadTramite';
import useRouteTramite from '@/hooks/useRouteTramite';
import FirmaSolicitud from '@/pages/TramiteSolicitudDetalle/FirmaSolicitud';
import SolicitudSummary from '@/pages/TramiteSolicitudDetalle/SolicitudSummary';

import { MESSAGE_PREVIEW_FILE } from '@/constants/file.constants';
import { ROUTES, ROUTES_LABEL } from '@/constants/routes.constants';
import { ESTADO_SUBSANACION, ESTADOS_OPERACION, TIPO_DOCUMENTO, TIPO_SUBSANACION } from '@/constants/tramite.constants';
import {
	filterDocumentosAdicionales,
	filterDocumentosRequeridos,
	filterSujetosAdicionales,
	filterSujetosRequeridos,
} from '@/helpers/formSolicitudes';
import { getBreadcrumbTramite } from '@/helpers/navigation';
import { type Action, makeActions } from '@/helpers/table';
import {
	type FormTramiteValues,
	type IDocumento,
	type ISolicitudDocumentacion,
	type ISujetoListado,
} from '@/interfaces/model/don.interface';

const TramiteSolicitudDetalle = () => {
	const navigate = useNavigate();
	const location = useLocation();
  	const fromSolicitudes = location.state?.fromSolcitudes;

	const { idTramite: id } = useParams();
	const ROUTES_TRAMITE = useRouteTramite();
	const { appendBreadcrumb, setUrlBack } = usePageContext();
	const { maestrosTramite, getTramiteData } = useGlobalContext();
	const { watch } = useFormContext<FormTramiteValues>();
	const { isLoading, reload } = useLoadTramite(Number(id), true);
	const modalSubsanacion = useModal({});
	const modalVencimiento = useModal({});
	const modalCancelacion = useModal({});
	const modalHistorialSolicitud = useModal({});
	const [dataModalPdf, setDataModalPdf] = useState<IDocumento | undefined>(undefined);
	const [dataSubsanacion, setDataSubsanacion] = useState<{
		isHasErrors?: boolean;
		idTipoSubsanacion: ISolicitudDocumentacion['tipo']['id'];
		idTipoDocumento?: number;
		idSubsanacion: ISolicitudDocumentacion['id'];
	}>();

	const isSubsanacionSujeto = dataSubsanacion?.idTipoSubsanacion === TIPO_SUBSANACION.SUJETO;
	const isSubsanacionDocumento =
		dataSubsanacion?.idTipoSubsanacion === TIPO_SUBSANACION.DOCUMENTO ||
		dataSubsanacion?.idTipoSubsanacion === TIPO_SUBSANACION.NUEVO_DOCUMENTO;

	const documentos = watch('documentos') ?? [];
	const sujetos = watch('sujetos') ?? [];

	// Cuando se muestra el detalle de los documentos, mostramos todos, vigentes y no vigentes.
	const documentosRequeridos = filterDocumentosRequeridos(documentos, maestrosTramite.tipoDocumento);
	// Cuando se muestra el detalle de los documentos, mostramos todos, vigentes y no vigentes.
	const documentosAdicionales = filterDocumentosAdicionales(documentos, maestrosTramite.tipoDocumento);
	// Cuando se muestra el detalle de los sujetos, mostramos todos, vigentes y no vigentes.
	const sujetosRequeridos = filterSujetosRequeridos(sujetos, maestrosTramite.tipoIntervencion);
	// Cuando se muestra el detalle de los sujetos, mostramos todos, vigentes y no vigentes.
	const sujetosAdicionales = filterSujetosAdicionales(sujetos, maestrosTramite.tipoIntervencion);
	const hasFechaVencimiento = maestrosTramite.configuracionTramite?.permiteVencimiento;
	const solicitudesDocumentacion = watch('solicitudesDocumentacion') ?? [];
	const estadoTramite = watch('estadoTramite');
	const idEstado = estadoTramite?.id;

	const onViewSolicitudDocumentacion = (rowData: ISolicitudDocumentacion) => {
		const { tramite, tipo, id } = rowData;
		return navigate(`${ROUTES_TRAMITE.SOLICITUDES_DOCUMENTACION_DETALLE}/${tramite.id}/${tipo.id}/${id}`.replace(':tab', 'ambas'),
			{ state: { from: getBreadcrumbTramite(getTramiteData()) } }
		);
	};

	const onSendDocumentacion = ({ id, tipo }: ISolicitudDocumentacion) => {
		setDataSubsanacion({
			idSubsanacion: id,
			idTipoSubsanacion: tipo.id,
			idTipoDocumento: TIPO_DOCUMENTO.OTROS,
		});
		modalSubsanacion.openModal();
	};

	const onSubsanarDocumento = ({ subsanacionPendiente, tipoDocumento }: IDocumento) => {
		setDataSubsanacion({
			idSubsanacion: subsanacionPendiente?.id as number,
			idTipoSubsanacion: subsanacionPendiente?.tipo.id as number,
			idTipoDocumento: tipoDocumento.id,
			isHasErrors: true,
		});
		modalSubsanacion.openModal();
	};

	const onSubsanarSujeto = ({ subsanacionPendiente }: ISujetoListado) => {
		setDataSubsanacion({
			idSubsanacion: subsanacionPendiente?.id as number,
			idTipoSubsanacion: subsanacionPendiente?.tipo.id as number,
		});
		modalSubsanacion.openModal();
	};

	const onSubmitSubsanacion = () => {
		modalSubsanacion.closeModal();
		reload();
	};

	const onSubmitCambiarVencimiento = () => {
		modalVencimiento.closeModal();
		reload();
	};

	const onSubmitCancelacion = () => {
		navigate(ROUTES_TRAMITE.SOLICITUDES);
	};

	const onViewDocumento = (value: IDocumento) => {
		const idTipoSubsanacion = value.subsanacionPendiente?.tipo.id;
		const idSubsanacion = value.subsanacionPendiente?.id;

		let path = generatePath(ROUTES_TRAMITE.DETALLE_DOCUMENTO, {
			idTramite: Number(id as string),
			idDocumento: value.id,
		});

		if (idTipoSubsanacion && idSubsanacion) {
			path = generatePath(ROUTES_TRAMITE.DETALLE_DOCUMENTO_SUBSANABLE, {
				idTramite: Number(id as string),
				idDocumento: value.id,
				idTipoSubsanacion,
				idSubsanacion,
			});
		}

		navigate(path);
	};

	const onViewSujeto = (data: ISujetoListado) => {		
		const path = generatePath(ROUTES_TRAMITE.DETALLE_SUJETO, {
			idTramite: Number(id as string),
			idSujeto: data.id,
			subsanacion: data?.subsanacionPendiente?.id ?? 0,
			idTipoSubsanacion: data?.subsanacionPendiente?.tipo?.id ?? 0,
		});

		navigate(path);
	};

	const solicitudesDocumentacionColumns = useMemo(() => {
		const actions: Array<Action<ISolicitudDocumentacion>> = [
			{
				width: 110,
				label: 'Ver detalle',
				command(rowData: ISolicitudDocumentacion) {
					onViewSolicitudDocumentacion(rowData);
				},
			},
			{
				width: 100,
				label: 'Enviar documento',
				command: onSendDocumentacion,
				hideIf: ({ estado }) => estado.id !== ESTADO_SUBSANACION.PENDIENTE,
			},
		];

		const columnsWithOutActions = getColumnsTramite();

		const actionsColumn = makeActions<ISolicitudDocumentacion>({ data: solicitudesDocumentacion, actions });
		
		return [...columnsWithOutActions, actionsColumn];
	}, [solicitudesDocumentacion]);

	useEffect(() => {
		appendBreadcrumb(ROUTES_LABEL.SOLICITUD, [
			getBreadcrumbTramite(getTramiteData()),
			{ label: ROUTES_LABEL.SOLICITUDES_BORRADORES, url: ROUTES_TRAMITE.SOLICITUDES },
			{ label: ROUTES_LABEL.SOLICITUD, url: location.pathname },
		]);	
		if (fromSolicitudes) {
			setUrlBack({label: 'Volver a solicitudes', navigate() {
				navigate(ROUTES.SOLICITUDES);
			}});
		}	
	}, []);

	return (
		<>
			<PageHeader hasHelp title="Detalle de la solicitud" className="u-mt-8" />
			<div className="[ c-pnp-box c-pnp-box--border c-pnp-box--breakout ] [ u-flow-border ] [ u-mt-24 ]">
				<div className="c-pnp-box__body p-confirm">
					<section className="[ c-pnp-form-group c-pnp-form-group--accent c-pnp-form-group--radius c-pnp-form-group--display-only ]">
						<SolicitudSummary title="Datos de la solicitud" />
					</section>
					{(idEstado === ESTADOS_OPERACION.FIRMADA || idEstado === ESTADOS_OPERACION.FINALIZADA) && (
						<section className="[ c-pnp-form-group__datos-firma c-pnp-form-group--radius ]">
							<FirmaSolicitud
								title="Información de la escritura"
								reload={reload}
							/>
						</section>
					)}
					<section className="u-mt-32">
						<header className="o-splitter">
							<TextIcon
								className="c-pnp-heading-h4"
								tag="h4"
								description="Documentación requerida"
								iconRight={{
									icon: 'info',
									color: 'blue-50',
									tooltip: { content: MESSAGE_PREVIEW_FILE, maxWidth: 432, place: 'top' },
								}}
							/>
						</header>
						<div className="u-mt-24 u-mt-16@portrait">
							<DocumentosListado
								hasColumnSubsanacion
								documentos={documentosRequeridos}
								idTramite={Number(id as string)}
								onViewDocumento={onViewDocumento}
								onSubsanar={onSubsanarDocumento}
							/>
						</div>
					</section>
					{documentosAdicionales.length > 0 && (
						<section className="u-mt-80 u-mt-32@portrait">
							<header className="o-splitter">
								<TextIcon
									className="c-pnp-heading-h4"
									tag="h4"
									description="Documentación adicional"
									iconRight={{
										icon: 'info',
										color: 'blue-50',
										tooltip: { content: MESSAGE_PREVIEW_FILE, maxWidth: 432, place: 'top' },
									}}
								/>
							</header>
							<div className="u-mt-24 u-mt-16@portrait">
								<DocumentosListado
									hasColumnSubsanacion
									documentos={documentosAdicionales}
									idTramite={Number(id as string)}
									noData="Aún no has añadido ningún documento adicional"
									onViewDocumento={onViewDocumento}
									onSubsanar={onSubsanarDocumento}
								/>
							</div>
						</section>
					)}
					<section className="u-mt-80 u-mt-32@portrait">
						<header className="o-splitter">
							<h4 className="c-pnp-heading-h4">Intervinientes requeridos</h4>
						</header>
						<div className="u-mt-24 u-mt-16@portrait">
							<IntervinientesListado
								hasColumnSubsanacion
								data={sujetosRequeridos}
								onView={onViewSujeto}
								onSubsanar={onSubsanarSujeto}
							/>
						</div>
					</section>
					{sujetosAdicionales.length > 0 && (
						<section className="u-mt-80 u-mt-32@portrait">
							<header className="o-splitter">
								<h4 className="c-pnp-heading-h4">Intervinientes adicionales</h4>
							</header>
							<div className="u-mt-24 u-mt-16@portrait">
								<IntervinientesListado
									hasColumnSubsanacion
									data={sujetosAdicionales}
									onView={onViewSujeto}
									onSubsanar={onSubsanarSujeto}
								/>
							</div>
						</section>
					)}
					{solicitudesDocumentacion.length > 0 && (
						<section className="u-mt-80 u-mt-32@portrait">
							<header className="o-splitter">
								<h4 className="c-pnp-heading-h4">Solicitudes de documentación</h4>
							</header>
							<div className="u-mt-24 u-mt-16@portrait">
								<SolicitudesDocumentacionListado
									columns={solicitudesDocumentacionColumns}
									data={solicitudesDocumentacion}
									totalData={solicitudesDocumentacion.length}
								/>
							</div>
						</section>
					)}
				</div>
				<footer className="c-pnp-box__footer">
					<GroupButtons
						left={[
							...(idEstado === ESTADOS_OPERACION.PENDIENTE
								? [
										{
											id: 'cancel',
											disabled: isLoading,
											variant: 'outline',
											mode: 'danger',
											minWidth: '140',
											label: 'Cancelar solicitud',
											onClick: modalCancelacion.openModal,
										} satisfies ButtonOfGroup,
									]
								: []),
						]}
						right={[
							...(idEstado === ESTADOS_OPERACION.PENDIENTE && hasFechaVencimiento
								? [
										{
											id: 'cambiarVencimiento',
											disabled: isLoading,
											mode: 'primary',
											minWidth: '140',
											label: 'Cambiar vencimiento',
											onClick: modalVencimiento.openModal,
										} satisfies ButtonOfGroup,
									]
								: []),
							{
								id: 'historial',
								disabled: isLoading,
								mode: 'secondary',
								minWidth: '140',
								label: 'Ver historial',
								onClick: modalHistorialSolicitud.openModal,
							},
						]}
					/>
				</footer>
			</div>
			{isSubsanacionDocumento && (
				<ModalSubsanacionDocumentos
					hasErrorDoc={dataSubsanacion.isHasErrors}
					data={{
						idTramite: Number(id as string),
						id: dataSubsanacion.idSubsanacion,
						idTipo: dataSubsanacion.idTipoSubsanacion,
					}}
					idTipoDocumento={dataSubsanacion.idTipoDocumento as number}
					isOpen={modalSubsanacion.isShowing}
					onClose={modalSubsanacion.closeModal}
					onSubmit={onSubmitSubsanacion}
				/>
			)}			
			{isSubsanacionSujeto && (
				<ModalSubsanacionInterviniente
					data={{
						idTramite: Number(id as string),
						id: dataSubsanacion.idSubsanacion,
						idTipo: dataSubsanacion.idTipoSubsanacion,
					}}
					isOpen={modalSubsanacion.isShowing}
					onClose={modalSubsanacion.closeModal}
					onSubmit={onSubmitSubsanacion}
				/>
			)}
			<ModalVisorPdf
				isOpen={Boolean(dataModalPdf)}
				data={dataModalPdf}
				idTramite={Number(id as string)}
				onClose={() => setDataModalPdf(undefined)}
			/>
			<ModalCambiarVencimiento
				isOpen={modalVencimiento.isShowing}
				closeModal={modalVencimiento.closeModal}
				onSave={onSubmitCambiarVencimiento}
			/>
			<ModalHistorialSolicitud
				isOpen={modalHistorialSolicitud.isShowing}
				onClose={modalHistorialSolicitud.closeModal}
			/>
			<ModalCancelacion
				isOpen={modalCancelacion.isShowing}
				onClose={modalCancelacion.closeModal}
				onSave={onSubmitCancelacion}
			/>
		</>
	);
};

export default TramiteSolicitudDetalle;
